import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { TextareaModule } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import {
  ControlPoint,
  StoreControlPointSchema,
} from '../../../../shared/models/control-points/control-point.model';
import { ControlPointService } from '../../../../shared/services/control-points/control-point.service';
import { QualificationService } from '../../../../shared/services/control-points/qualification.service';
import { HasUnsavedChanges } from '../../../../shared/guards/unsaved-changes.guard';
import {
  applyServerErrors,
  applyZodErrors,
} from '../../../../shared/util/zod-form-errors';

@Component({
  selector: 'app-control-point-detail',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    RouterModule,
    ReactiveFormsModule,
    TranslateModule,
    CardModule,
    ButtonModule,
    InputTextModule,
    InputNumberModule,
    TextareaModule,
    MultiSelectModule,
    ToastModule,
    ConfirmDialogModule,
  ],
  providers: [MessageService, ConfirmationService],
  templateUrl: './control-point-detail.component.html',
  styleUrl: './control-point-detail.component.scss',
})
export class ControlPointDetailComponent implements OnInit, HasUnsavedChanges {
  service = inject(ControlPointService);
  qualificationService = inject(QualificationService);
  #messageService = inject(MessageService);
  #confirmationService = inject(ConfirmationService);
  #translate = inject(TranslateService);
  #route = inject(ActivatedRoute);
  #router = inject(Router);
  #fb = inject(FormBuilder);

  model = signal<ControlPoint | null>(null);
  isEditMode = signal(false);
  loading = signal(false);
  submitting = signal(false);
  #saved = signal(false);

  form: FormGroup = this.#fb.group({
    name: ['', [Validators.required, Validators.maxLength(255)]],
    description: ['' as string | null],
    interval_minutes: [
      60 as number | null,
      [Validators.required, Validators.min(1), Validators.max(480)],
    ],
    tag_name: ['', [Validators.required, Validators.maxLength(255)]],
    qualification_ids: [[] as number[]],
  });

  ngOnInit(): void {
    this.qualificationService.getAll();
    const idParam = this.#route.snapshot.paramMap.get('id');
    if (idParam && idParam !== 'new') {
      this.isEditMode.set(true);
      this.#loadModel(Number(idParam));
    }
  }

  hasUnsavedChanges(): boolean {
    return this.form.dirty && !this.#saved();
  }

  #loadModel(id: number) {
    this.loading.set(true);
    this.service.getById(id).subscribe({
      next: (response) => {
        this.loading.set(false);
        const item = response.data;
        this.model.set(item);
        this.form.patchValue({
          name: item.name,
          description: item.description ?? '',
          interval_minutes: item.interval_minutes,
          tag_name: item.tag_name,
          qualification_ids: (item.required_qualifications ?? [])
            .map((q: { id?: number }) => q?.id)
            .filter((qid): qid is number => typeof qid === 'number'),
        });
        this.form.markAsPristine();
      },
      error: (e: { status?: number }) => {
        this.loading.set(false);
        if (e?.status === 404) {
          this.#messageService.add({
            severity: 'error',
            summary: this.#translate.instant('label.errorHeader'),
            detail: this.#translate.instant(
              'controlPoints.controlPoints.notFound',
            ),
          });
          this.#router.navigate(['/control-points/control-points']);
        } else {
          this.#messageService.add({
            severity: 'error',
            summary: this.#translate.instant('label.errorHeader'),
            detail: this.#translate.instant(
              'controlPoints.controlPoints.loadError',
            ),
          });
        }
      },
    });
  }

  onSubmit() {
    if (this.submitting()) return;
    if (!this.form.valid) {
      this.form.markAllAsTouched();
      return;
    }
    const raw = this.form.getRawValue();
    const parsed = StoreControlPointSchema.safeParse({
      name: raw.name,
      description: raw.description?.trim() ? raw.description : null,
      interval_minutes: raw.interval_minutes,
      tag_name: raw.tag_name?.trim() ?? raw.tag_name,
      qualification_ids: raw.qualification_ids ?? [],
    });
    if (!parsed.success) {
      applyZodErrors(parsed.error, this.form);
      return;
    }

    this.submitting.set(true);
    const editing = this.model();

    const call$ = editing
      ? this.service.update(editing.id, parsed.data)
      : this.service.create(parsed.data);

    call$.subscribe({
      next: (response) => {
        this.submitting.set(false);
        this.#saved.set(true);
        this.model.set(response.data);
        this.form.markAsPristine();
        this.#messageService.add({
          severity: 'success',
          summary: this.#translate.instant('label.successHeader'),
          detail: this.#translate.instant(
            editing
              ? 'controlPoints.controlPoints.updated'
              : 'controlPoints.controlPoints.created',
          ),
        });
        if (!editing) {
          this.isEditMode.set(true);
          this.#router.navigate([
            '/control-points/control-points',
            response.data.id,
          ]);
        }
      },
      error: (e: unknown) => {
        this.submitting.set(false);
        applyServerErrors(e, this.form);
        this.#messageService.add({
          severity: 'error',
          summary: this.#translate.instant('label.errorHeader'),
          detail: this.#translate.instant(
            editing
              ? 'controlPoints.controlPoints.updateError'
              : 'controlPoints.controlPoints.createError',
          ),
        });
      },
    });
  }

  onDelete(event: Event) {
    const item = this.model();
    if (!item) return;
    this.#confirmationService.confirm({
      target: event.target as EventTarget,
      header: this.#translate.instant(
        'controlPoints.controlPoints.deleteConfirmHeader',
      ),
      message: this.#translate.instant(
        'controlPoints.controlPoints.deleteConfirmMessage',
      ),
      acceptLabel: this.#translate.instant('label.delete'),
      rejectLabel: this.#translate.instant('label.cancel'),
      acceptButtonProps: { severity: 'danger' },
      accept: () => {
        this.service.delete(item.id).subscribe({
          next: () => {
            this.#saved.set(true);
            this.#messageService.add({
              severity: 'success',
              summary: this.#translate.instant('label.successHeader'),
              detail: this.#translate.instant(
                'controlPoints.controlPoints.deleted',
              ),
            });
            this.#router.navigate(['/control-points/control-points']);
          },
          error: () => {
            this.#messageService.add({
              severity: 'error',
              summary: this.#translate.instant('label.errorHeader'),
              detail: this.#translate.instant(
                'controlPoints.controlPoints.deleteError',
              ),
            });
          },
        });
      },
    });
  }

  onCancel() {
    this.#router.navigate(['/control-points/control-points']);
  }
}
